/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import SubmissionStatus from '../SubmissionStatus';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const WEB3FORMS_ACCESS_KEY =
  process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '';
const CONTACT_FORM_SUBJECT = 'New BroCher Builds contact form submission';
const CONTACT_SUBMISSION_STATUS_KEY = 'contactSubmissionStatus';

const getWeb3FormsPayload = ({
  firstName,
  lastName,
  email,
  phone,
  budget,
  referral,
  message,
  botcheck,
}) => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return {
    access_key: WEB3FORMS_ACCESS_KEY,
    subject: CONTACT_FORM_SUBJECT,
    from_name: fullName || 'BroCher Builds website',
    replyto: email,
    first_name: firstName,
    last_name: lastName,
    full_name: fullName,
    name: fullName,
    email,
    phone,
    budget,
    referral,
    message: message || 'No message provided.',
    botcheck,
  };
};

const getSubmissionErrorMessage = (error) => {
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return 'Unable to send your request right now. Please try again later.';
};

const validationSchema = yup.object({
  firstName: yup
    .string('Enter your first name')
    .trim()
    .min(2, 'Please enter a valid name')
    .max(50, 'Please enter a valid name')
    .required('Please specify your first name'),
  lastName: yup
    .string('Enter your last name')
    .trim()
    .min(2, 'Please enter a valid name')
    .max(50, 'Please enter a valid name')
    .required('Please specify your last name'),
  email: yup
    .string('Enter your email')
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required.'),
  phone: yup
    .string()
    .trim()
    .matches(
      /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)$/,
      'Please enter a valid phone number.',
    )
    .required('Please enter your phone number'),
  budget: yup.string().required('Please specify your project budget'),
  referral: yup.string().required('How did you hear about us?'),
  message: yup
    .string()
    .trim()
    .max(500, 'The message cannot contain more than 500 characters'),
});

const Form = () => {
  const [submissionState, setSubmissionState] = useState(null);
  const [submissionError, setSubmissionError] = useState('');

  const setSubmissionStatus = (status) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (status) {
      window.sessionStorage.setItem(CONTACT_SUBMISSION_STATUS_KEY, status);
      return;
    }

    window.sessionStorage.removeItem(CONTACT_SUBMISSION_STATUS_KEY);
  };

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    budget: '',
    referral: '',
    message: '',
    botcheck: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm, setStatus, setSubmitting }) => {
      setStatus(undefined);
      setSubmissionStatus('pending');
      setSubmissionError('');

      if (!WEB3FORMS_ACCESS_KEY.trim()) {
        setSubmissionStatus('error');
        setSubmissionState('error');
        setSubmissionError(
          'The contact form is not configured yet. Please call us directly so we can help you right away.',
        );
        setSubmitting(false);
        return;
      }

      try {
        const payload = getWeb3FormsPayload(values);
        const response = await fetch(WEB3FORMS_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();

        if (!response.ok || !result?.success) {
          throw new Error(
            result?.message || 'Web3Forms rejected the request.',
          );
        }

        resetForm();
        setSubmissionStatus('submitted');
        setSubmissionState('submitted');
      } catch (error) {
        const errorMessage = getSubmissionErrorMessage(error);
        console.error('Web3Forms submission failed:', error);
        setSubmissionStatus('error');
        setSubmissionState('error');
        setSubmissionError(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (submissionState) {
    return (
      <SubmissionStatus
        status={submissionState}
        errorMessage={submissionError}
      />
    );
  }

  return (
    <Box>
      <Box component={'div'}>
        <input
          type={'text'}
          name={'botcheck'}
          value={formik.values.botcheck}
          onChange={formik.handleChange}
          tabIndex={-1}
          autoComplete={'off'}
          style={{ display: 'none' }}
        />
        <Box
          component={Grid}
          marginBottom={{ xs: 10, sm: 0 }}
          container
          spacing={4}
        >
          <Grid item xs={12} sm={6}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Please tell us your name *
            </Typography>
            <TextField
              label="First name"
              variant="outlined"
              name={'firstName'}
              fullWidth
              value={formik.values.firstName}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Please tell us your name *
            </Typography>
            <TextField
              label="Last name"
              variant="outlined"
              name={'lastName'}
              fullWidth
              value={formik.values.lastName}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Please enter your email address *
            </Typography>
            <TextField
              label="Email"
              variant="outlined"
              name={'email'}
              fullWidth
              value={formik.values.email}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Please enter your phone number *
            </Typography>
            <TextField
              label="Phone number"
              variant="outlined"
              name={'phone'}
              fullWidth
              value={formik.values.phone}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Please tell us the budget *
            </Typography>
            <TextField
              select
              label="Project budget"
              variant="outlined"
              name={'budget'}
              fullWidth
              value={formik.values.budget}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.budget && Boolean(formik.errors.budget)}
              helperText={formik.touched.budget && formik.errors.budget}
            >
              {[
                '< $20,000',
                '$20,000 - $50,000',
                '$50,000 - $100,000',
                '> $100,000',
              ].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              How did you hear about us *
            </Typography>
            <TextField
              select
              label="How did you find us"
              variant="outlined"
              name={'referral'}
              fullWidth
              value={formik.values.referral}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.referral && Boolean(formik.errors.referral)}
              helperText={formik.touched.referral && formik.errors.referral}
            >
              {[
                'Google',
                'Facebook',
                'Instagram',
                'Twitter',
                'Friend',
                'Other',
              ].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Please tell us about your project (optional)
            </Typography>
            <TextField
              label="Message"
              variant="outlined"
              name={'message'}
              fullWidth
              multiline
              rows={4}
              value={formik.values.message}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
            />
          </Grid>
          <Grid
            item
            container
            xs={12}
            justifyContent={'center'}
            alignItems={'center'}
            flexDirection={'column'}
          >
            <Button
              size={'large'}
              variant={'contained'}
              type={'button'}
              onClick={formik.submitForm}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? 'Sending...' : 'Send request'}
            </Button>
            {formik.status ? (
              <Typography
                variant={'subtitle2'}
                color={'error'}
                sx={{ marginTop: 2 }}
                align={'center'}
              >
                {formik.status}
              </Typography>
            ) : null}
            <Typography
              variant={'subtitle2'}
              color={'textSecondary'}
              sx={{ marginTop: 2 }}
              align={'center'}
            >
              We'll get back to you in 1-2 business days.
            </Typography>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Form;
